import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './ResultsPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://wwh9l4i5kl.execute-api.us-east-1.amazonaws.com';

const ResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('heart-rate-section');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  const analysisData = location.state?.analysisData;

  // Construct full plot URL from API base URL and plot_path
  const getPlotUrl = () => {
    if (analysisData?.analysis_info?.plot_path) {
      return `${API_URL}${analysisData.analysis_info.plot_path}`;
    }
    return null;
  };

  const plotUrl = getPlotUrl();

  if (!analysisData) {
    return (
      <div className="error-container">
        <h1>No Analysis Data Available</h1>
        <p>Please go back and select a file first.</p>
        <button onClick={() => navigate('/')} className="back-to-upload-btn">
          Back to Selection
        </button>
      </div>
    );
  }

  // Determine severity level from detailed_findings
  let severityLevel = 'normal';
  if (analysisData.detailed_findings && analysisData.detailed_findings.length > 0) {
    const severities = analysisData.detailed_findings.map(f => f.severity?.toLowerCase());
    if (severities.includes('high')) {
      severityLevel = 'high';
    } else if (severities.includes('medium') || severities.includes('moderate')) {
      severityLevel = 'medium';
    } else if (severities.includes('low')) {
      severityLevel = 'low';
    }
  }

  const getSeverityClass = (severity) => {
    const sev = severity?.toLowerCase();
    if (sev === 'high') return 'high';
    if (sev === 'medium' || sev === 'moderate') return 'medium';
    if (sev === 'low') return 'low';
    return 'normal';
  };

  const getSeverityColor = () => {
    switch (severityLevel) {
      case 'high':
        return '#c33';
      case 'medium':
        return '#ffc107';
      case 'low':
        return '#28a745';
      default:
        return '#17a2b8';
    }
  };

  const getSeverityBackground = () => {
    switch (severityLevel) {
      case 'high':
        return '#fee';
      case 'medium':
        return '#fff3cd';
      case 'low':
        return '#d4edda';
      default:
        return '#d1ecf1';
    }
  };

  const handleNavClick = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const goBack = () => {
    navigate('/');
  };

  const openFullscreen = () => {
    setFullscreenImage(true);
    document.body.style.overflow = 'hidden';
  };

  const closeFullscreen = () => {
    setFullscreenImage(false);
    document.body.style.overflow = 'auto';
  };

  return (
    <div className={`results-page ${severityLevel}`}>
      {/* Mobile Navigation Bar */}
      <div className="mobile-nav">
        <div className="mobile-nav-left">
          <button className="hamburger-btn" onClick={toggleSidebar}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
            </svg>
          </button>
          <div className="mobile-logo">Heartbeat Analyzer</div>
        </div>
        <button className="mobile-back" onClick={goBack}>
          Back to Selection
        </button>
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)}></div>}

      {/* Left Sidebar */}
      <div className={`sidebar ${sidebarOpen ? 'active' : ''}`}>
        <div className="sidebar-header">
          <div className="logo">Heartbeat Analyzer</div>
          <div className="logo-byline">Trusted AI for Smarter Healthcare.</div>
          <button className="back-btn" onClick={goBack}>
            ← Back to Selection
          </button>
        </div>

        <div className="nav-menu">
          <h3>Analysis Results</h3>
          <div
            className={`nav-item ${activeSection === 'heart-rate-section' ? 'active' : ''}`}
            onClick={() => handleNavClick('heart-rate-section')}
          >
            Heart Rate Analysis
          </div>
          <div
            className={`nav-item ${activeSection === 'chart-section' ? 'active' : ''}`}
            onClick={() => handleNavClick('chart-section')}
          >
            ECG Waveform
          </div>
          <div
            className={`nav-item ${activeSection === 'findings-section' ? 'active' : ''}`}
            onClick={() => handleNavClick('findings-section')}
          >
            Clinical Findings
          </div>
          <div
            className={`nav-item ${activeSection === 'disclaimer-section' ? 'active' : ''}`}
            onClick={() => handleNavClick('disclaimer-section')}
          >
            Important Notes
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="main-content">
        <div className="header">
          <h1 className="page-title">Analysis Results</h1>
          <p className="page-subtitle">
            Heart rate analysis completed using {analysisData.signal_analysis?.method || 'BioSPPy'} methodology
          </p>
        </div>

        <div className="content-area">
          <div className="results-container">
            {/* Chart Section */}
            <div className="chart-section" id="chart-section">
              <div className="chart-header">
                <div className="chart-title">Heart Rate Visualization</div>
                <div className="chart-subtitle">ECG waveform analysis and rhythm detection</div>
              </div>
              <div className="chart-content">
                {plotUrl ? (
                  <>
                    {imageLoading && (
                      <div className="chart-loader">
                        <div className="chart-spinner"></div>
                        <span>Loading chart...</span>
                      </div>
                    )}
                    <img
                      src={plotUrl}
                      alt="Heart Rate Analysis Chart"
                      className="chart-image"
                      style={{ display: imageLoading ? 'none' : 'block' }}
                      onClick={openFullscreen}
                      onLoad={() => setImageLoading(false)}
                      onError={(e) => {
                        setImageLoading(false);
                        e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='200'%3E%3Crect width='400' height='200' fill='%23f8f9fa'/%3E%3Ctext x='200' y='100' text-anchor='middle' fill='%23666'%3EChart not available%3C/text%3E%3C/svg%3E";
                      }}
                    />
                  </>
                ) : (
                  <div className="no-chart">Chart not available</div>
                )}
                <div className="fullscreen-hint">Click image to view fullscreen</div>
              </div>
            </div>

            {/* Analysis Summary */}
            <div className="analysis-section" id="heart-rate-section">
              <div className="analysis-header">
                <div className="analysis-title">Key Metrics</div>
              </div>
              <div className="analysis-content">
                <div className="metric-row">
                  <span className="metric-label">Average Heart Rate</span>
                  <span className="metric-value">
                    {analysisData.heart_rate?.average_bpm?.toFixed(1) || 'N/A'} BPM
                  </span>
                </div>
                <div className="metric-row">
                  <span className="metric-label">S1 Sounds Detected</span>
                  <span className="metric-value">{analysisData.heart_sounds?.num_s1 || 'N/A'}</span>
                </div>
                <div className="metric-row">
                  <span className="metric-label">S2 Sounds Detected</span>
                  <span className="metric-value">{analysisData.heart_sounds?.num_s2 || 'N/A'}</span>
                </div>
                <div className="metric-row">
                  <span className="metric-label">Heart Rate Variability</span>
                  <span className="metric-value">
                    {analysisData.heart_rate?.hr_std?.toFixed(2) || 'N/A'}
                  </span>
                </div>
                <div className="metric-row">
                  <span className="metric-label">Irregularity Score</span>
                  <span className="metric-value">
                    {analysisData.heart_rate?.irregularity_score?.toFixed(3) || 'N/A'}
                  </span>
                </div>

                <div
                  className={`classification ${severityLevel}`}
                  style={{
                    backgroundColor: getSeverityBackground(),
                    color: getSeverityColor(),
                    borderLeftColor: getSeverityColor()
                  }}
                >
                  <div className="classification-title">
                    Classification: {analysisData.classification?.result || 'Unknown'}
                  </div>
                  <div className="confidence">
                    Confidence: {analysisData.classification?.confidence_percentage || 'N/A'}
                  </div>
                </div>
              </div>
            </div>

            {/* Detailed Findings */}
            <div className="findings-section" id="findings-section">
              <div className="findings-header">
                <div className="analysis-title">Detailed Clinical Findings</div>
              </div>
              <div className="findings-content">
                {analysisData.detailed_findings && analysisData.detailed_findings.length > 0 ? (
                  analysisData.detailed_findings.map((finding, index) => (
                    <div key={index} className={`finding-item ${getSeverityClass(finding.severity)}`}>
                      <div className="finding-title">{index + 1}. {finding.condition}</div>
                      <span className={`finding-severity severity-${getSeverityClass(finding.severity)}`}>
                        {finding.severity} Severity
                      </span>
                      <div className="finding-description">{finding.description}</div>
                      <div className="finding-recommendation">
                        <strong>Recommendation:</strong> {finding.recommendation}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="finding-item normal">
                    <div className="finding-title">No specific findings</div>
                    <div className="finding-description">Analysis completed successfully with no significant abnormalities detected.</div>
                  </div>
                )}

                {/* Summary based on severity */}
                <div
                  className={`summary ${severityLevel}-summary`}
                  style={{
                    backgroundColor: getSeverityBackground(),
                    borderColor: getSeverityColor(),
                    borderLeftColor: getSeverityColor()
                  }}
                >
                  <h4 style={{ color: getSeverityColor(), fontWeight: 600, marginBottom: '8px' }}>
                    Summary Recommendations:
                  </h4>
                  {analysisData.recommendations && analysisData.recommendations.length > 0 ? (
                    <ul style={{ color: getSeverityColor(), fontWeight: 500, margin: 0, paddingLeft: '16px' }}>
                      {analysisData.recommendations.map((rec, index) => (
                        <li key={index}>{rec}</li>
                      ))}
                    </ul>
                  ) : (
                    <p style={{ color: getSeverityColor(), fontWeight: 500 }}>
                      • Follow standard medical guidance for your condition
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Important Disclaimers */}
            <div className="disclaimer" id="disclaimer-section">
              <div className="disclaimer-title">Important Disclaimers</div>
              <div className="disclaimer-text">
                <strong>This analysis is for educational and research purposes only.</strong>
                <ul>
                  {analysisData.disclaimers && analysisData.disclaimers.length > 0 ? (
                    analysisData.disclaimers.map((disclaimer, index) => (
                      <li key={index}>{disclaimer}</li>
                    ))
                  ) : (
                    <>
                      <li>Results should NOT be used for medical diagnosis or treatment decisions</li>
                      <li>Always consult qualified healthcare professionals for medical concerns</li>
                      <li>The analysis may have false positives/negatives - professional evaluation required</li>
                      <li>This is an automated tool and cannot replace professional medical assessment</li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fullscreen Image Overlay */}
      {fullscreenImage && (
        <div className="fullscreen-overlay active" onClick={closeFullscreen}>
          <div className="fullscreen-close" onClick={closeFullscreen}>&times;</div>
          {plotUrl && (
            <img
              src={plotUrl}
              alt="Heart Rate Analysis Chart - Fullscreen"
              className="fullscreen-image"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ResultsPage;
